import { useState, useEffect, useRef } from "react";
import { supabase, BRAND, BRAND_LIGHT, MUTED, FAINT, text, btn, btnSm, chip, linkBtn, iconBtn, errorText, errorBanner, savedText, SHADOW_MODAL, SHADOW_PANEL, SCRIM, LINE, WHITE, BRAND_DARK, INK, ROW_LINE, SURFACE, TEXT, HOVER_BG } from "./constants";
import { Settings2, X } from "lucide-react";
import SimpleGuide from "./SimpleGuide";
import InterviewMode from "./InterviewMode";
import { useIsMobile } from "./lib/responsive";
import { CandidateAvatar, CloseButton, ConfirmBar, ConfirmDialog, evalCriteria, evalScorePct, EVAL_SECTIONS, defaultEvalSections, defaultEvalItems } from "./shared";

// ─── PESTAÑA EVALUACIÓN DE LA POSICIÓN ────────────────────────────────────────
// Izquierda: formulario rápido para puntuar una entrevista (candidato,
// entrevistador, los 8 criterios 1-3 y comentarios). Derecha: todas las
// entrevistas de la posición, agrupadas por candidato, con su porcentaje.
//
// Escribe en los MISMOS campos que la pestaña Evaluate de la ficha
// (candidates.eval_managers / eval_scores / eval_notes), con la misma forma:
// un entrevistador es { id: "m<timestamp>", name, role } y sus notas y
// puntuaciones cuelgan de ese id. Lo que se guarda aquí se ve allí y al revés.
//
// Antes de escribir se relee la fila del candidato: si alguien evaluó desde la
// ficha mientras tanto, no se pisa su entrevista.

// Las preguntas son las de la posición (positions.eval_questions) o las 8 por
// defecto. El % de una entrevista es la suma sobre el máximo de las preguntas
// PUNTUADAS: es la misma cuenta que la media /5 de la ficha (evalScorePct).

// Escala de color de puntuación: de BRAND_LIGHT (peor) a BRAND (mejor). El
// mínimo posible con la escala 1-3 es 33%, así que la escala empieza ahí para
// que las diferencias reales se vean. Ver DESIGN_SYSTEM.md, "Escala de
// puntuación".
export const heat = (pct) => {
  const t = Math.max(0, Math.min(1, (pct - 33) / 67));
  const from = [0xEB, 0xEB, 0xFF], to = [0x3E, 0x3E, 0xF8];
  const c = from.map((f, i) => Math.round(f + (to[i] - f) * t));
  return { background: `rgb(${c.join(",")})`, color: t > 0.5 ? WHITE : BRAND_DARK };
};

const PctChip = ({ pct, big = false }) => pct === null ? <span style={{ color: FAINT, fontSize: 12 }}>—</span> : (
  <span style={{ ...heat(pct), display: "inline-block", minWidth: big ? 48 : 40, textAlign: "center", borderRadius: 999, padding: big ? "3px 10px" : "2px 8px", fontSize: big ? 12 : 11, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
    {Math.round(pct)}%
  </span>
);

// Editor de las preguntas de evaluación de la posición. Trabaja sobre un
// borrador: nada se guarda hasta "Guardar preguntas". Las secciones son los 4
// vectores (se renombran, no se añaden); la sección de una pregunta ya
// guardada no se cambia, porque su vector va en el id y moverla
// reasignaría las puntuaciones que ya tiene.
const QuestionsModal = ({ position, lang, onClose, onSaved }) => {
  const es = lang === "es";
  const cfg = position.evalQuestions;
  const [sections, setSections] = useState(() => {
    const base = defaultEvalSections(lang);
    EVAL_SECTIONS.forEach(s => { const v = cfg?.sections?.[s]; if (typeof v === "string" && v.trim()) base[s] = v.trim(); });
    return base;
  });
  const [items, setItems] = useState(() =>
    (Array.isArray(cfg?.items) && cfg.items.length ? cfg.items : defaultEvalItems(lang)).map(it => ({ id: it.id, label: it.label, section: String(it.id).slice(0, 2), isNew: false })));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const isMobile = useIsMobile();
  // En móvil: 44 de alto y 16px (iOS amplía la página por debajo de 16)
  const control = { height: isMobile ? 44 : 38, boxSizing: "border-box", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: isMobile ? 16 : 13, fontFamily: "inherit", color: INK, background: WHITE, outline: "none", padding: "0 10px" };
  const ordered = EVAL_SECTIONS.flatMap(s => items.filter(it => it.section === s));

  const save = async () => {
    const clean = items.filter(it => it.label.trim());
    if (clean.length === 0) { setErr(es ? "Tiene que quedar al menos una pregunta." : "Keep at least one question."); return; }
    const stamp = Date.now();
    const payload = {
      sections: Object.fromEntries(EVAL_SECTIONS.map(s => [s, (sections[s] || "").trim() || defaultEvalSections(lang)[s]])),
      items: EVAL_SECTIONS.flatMap(s => clean.filter(it => it.section === s)).map((it, i) => ({
        id: it.isNew ? `${it.section}q${stamp}${i}` : it.id, label: it.label.trim(),
      })),
    };
    setSaving(true); setErr("");
    const { data, error } = await supabase.from("positions").update({ eval_questions: payload }).eq("id", position.id).select("id");
    setSaving(false);
    if (error || !data || data.length === 0) {
      setErr((es ? "No se guardaron las preguntas. " : "Questions not saved. ") + (error?.message || (es ? "Sin permiso para editar la posición." : "No permission to edit the position.")));
      return;
    }
    onSaved(payload);
  };

  const title = es ? "Preguntas de la evaluación" : "Evaluation questions";

  // Cuerpo común a las dos versiones (ventana en escritorio, hoja a pantalla
  // completa en móvil)
  const body = (
    <>
        <p style={{ fontSize: 12, color: MUTED, margin: "0 0 18px", lineHeight: 1.6 }}>
          {es
            ? "Edita el texto, añade preguntas o elimina las que no apliquen. Valen para todas las entrevistas de esta posición. El % se calcula sobre las preguntas puntuadas en cada entrevista; las puntuaciones de una pregunta eliminada dejan de contar."
            : "Edit the text, add questions or remove the ones that don't apply. They apply to every interview in this position. The % is computed over the questions scored in each interview; scores for a removed question stop counting."}
        </p>

        <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>{es ? "Secciones" : "Sections"}</div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 8, marginBottom: 20 }}>
          {EVAL_SECTIONS.map((s, i) => (
            <div key={s} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: FAINT, minWidth: 14 }}>{i + 1}</span>
              <input value={sections[s]} onChange={e => setSections(p => ({ ...p, [s]: e.target.value }))} style={{ ...control, flex: 1 }}
                onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
            </div>
          ))}
        </div>

        <div style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>{es ? "Preguntas" : "Questions"}</div>
        {ordered.map(it => (
          <div key={it.id} style={isMobile
            ? { display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${ROW_LINE}` }
            : { display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <select value={it.section} disabled={!it.isNew}
              title={!it.isNew ? (es ? "La sección de una pregunta ya guardada no se cambia: sus puntuaciones son de ese vector." : "A saved question's section can't change: its scores belong to that vector.") : ""}
              onChange={e => { const v = e.target.value; setItems(p => p.map(x => x.id === it.id ? { ...x, section: v } : x)); }}
              style={{ ...control, width: isMobile ? "100%" : 170, flexShrink: 0, cursor: it.isNew ? "pointer" : "default", color: it.isNew ? INK : MUTED, background: it.isNew ? WHITE : SURFACE }}>
              {EVAL_SECTIONS.map(s => <option key={s} value={s}>{sections[s] || defaultEvalSections(lang)[s]}</option>)}
            </select>
            <input value={it.label} autoFocus={it.isNew && !it.label} placeholder={es ? "Texto de la pregunta" : "Question text"}
              onChange={e => { const v = e.target.value; setItems(p => p.map(x => x.id === it.id ? { ...x, label: v } : x)); }}
              style={{ ...control, flex: 1, minWidth: 0 }}
              onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
            <button onClick={() => setItems(p => p.filter(x => x.id !== it.id))} title={es ? "Quitar pregunta" : "Remove question"} aria-label={es ? "Quitar pregunta" : "Remove question"}
              style={{ ...iconBtn, color: FAINT, ...(isMobile ? { width: 40, height: 40 } : null) }}><X size={16} strokeWidth={1.75} /></button>
          </div>
        ))}
        <button onClick={() => setItems(p => [...p, { id: `new-${Date.now()}`, label: "", section: "c1", isNew: true }])}
          style={{ width: "100%", height: isMobile ? 44 : 38, marginTop: 4, borderRadius: 10, border: `1.5px dashed ${BRAND}`, background: WHITE, color: BRAND, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          + {es ? "Añadir pregunta" : "Add question"}
        </button>

        {err && <div style={{ ...errorText, marginTop: 12 }}>{err}</div>}
    </>
  );

  const resetDefaults = () => { setSections(defaultEvalSections(lang)); setItems(defaultEvalItems(lang).map(it => ({ ...it, section: it.id.slice(0, 2), isNew: false }))); setErr(""); };

  // Móvil: hoja a pantalla completa con cabecera propia, scroll propio y la
  // acción principal abajo a todo el ancho. Cerrar = la X de la cabecera.
  if (isMobile) {
    return (
      <div style={{ position: "fixed", inset: 0, background: WHITE, zIndex: 500, display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 8px 6px 16px", borderBottom: `1px solid ${LINE}`, flexShrink: 0 }}>
          <span style={text.cardTitle}>{title}</span>
          <CloseButton onClick={() => !saving && onClose()} label={es ? "Cerrar" : "Close"} />
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 16px 20px" }}>
          {body}
        </div>
        <div style={{ flexShrink: 0, borderTop: `1px solid ${LINE}`, padding: "12px 16px calc(12px + env(safe-area-inset-bottom))", display: "flex", flexDirection: "column", gap: 4, background: WHITE }}>
          <button onClick={save} disabled={saving} style={{ ...btn.primary, width: "100%", height: 48, padding: 0, fontSize: 15, cursor: saving ? "wait" : "pointer" }}>
            {saving ? (es ? "Guardando..." : "Saving...") : (es ? "Guardar preguntas" : "Save questions")}
          </button>
          <button onClick={resetDefaults}
            style={{ ...linkBtn, color: MUTED, fontWeight: 600, height: 40, fontSize: 13 }}>
            {es ? "Volver a las 8 por defecto" : "Reset to the default 8"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div onClick={() => !saving && onClose()} style={{ position: "fixed", inset: 0, background: SCRIM, zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: WHITE, borderRadius: 16, padding: "28px 30px", width: "100%", maxWidth: 680, maxHeight: "88vh", overflowY: "auto", boxShadow: SHADOW_MODAL }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 6 }}>
          <span style={text.cardTitle}>{title}</span>
          <CloseButton onClick={onClose} label={es ? "Cerrar" : "Close"} />
        </div>
        {body}

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 20, paddingTop: 16, borderTop: `1px solid ${LINE}` }}>
          <button onClick={resetDefaults}
            style={{ ...linkBtn, color: MUTED, fontWeight: 600 }}>
            {es ? "Volver a las 8 por defecto" : "Reset to the default 8"}
          </button>
          <button onClick={onClose} style={{ ...btn.ghost, marginLeft: "auto" }}>{es ? "Cancelar" : "Cancel"}</button>
          <button onClick={save} disabled={saving} style={{ ...btn.primary, cursor: saving ? "wait" : "pointer" }}>
            {saving ? (es ? "Guardando..." : "Saving...") : (es ? "Guardar preguntas" : "Save questions")}
          </button>
        </div>
      </div>
    </div>
  );
};

const EMPTY = { candidateId: "", name: "", scores: {}, notes: "" };

// onlyCandidate: modo ficha. El mismo formulario y la misma tabla, pero solo
// para ese candidato (la ficha de candidato usa este componente para que la
// evaluación tenga un único formato en toda la app). onEvalChange avisa a la
// ficha cuando cambia su evaluación.
const PositionEvaluationTab = ({ position, lang, photoUrls = {}, onOpenCandidate = () => {}, setOpenPositionsList = () => {}, onlyCandidate = null, onEvalChange = () => {} }) => {
  const es = lang === "es";
  const blank = onlyCandidate ? { ...EMPTY, candidateId: String(onlyCandidate.id) } : EMPTY;
  const [rows, setRows] = useState(null); // null = cargando
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null); // null | { candidateId, managerId }
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { ok, text }
  const [confirmDel, setConfirmDel] = useState(null); // { c, m }
  const [editQuestions, setEditQuestions] = useState(false);
  // Panel lateral con la guía de entrevista de un candidato. No tapa el
  // formulario: se lee la guía y se evalúa a la vez.
  const [guideFor, setGuideFor] = useState(null);
  // Vector al que salta la guía desde el botón WTA (What to ask)
  const [guideFocus, setGuideFocus] = useState(null);
  // Modo entrevista (21/10): pantalla completa con la guía y la puntuación juntas
  const [interviewFor, setInterviewFor] = useState(null);
  const isMobile = useIsMobile();
  // En móvil el formulario va encima de los resultados: al pulsar Editar en
  // una entrevista se sube hasta él.
  const formRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setRows(null); setLoadError(false);
    supabase.from("candidates").select("id, eval_managers, eval_scores, eval_notes, eval_updated_at").eq("position_id", position.id)
      .then(({ data, error }) => {
        if (!alive) return;
        if (error || !data) { setLoadError(true); setRows([]); return; }
        setRows(data);
      });
    return () => { alive = false; };
  }, [position.id]);

  const categories = evalCriteria(lang, position.evalQuestions);
  const items = categories.flatMap(cat => cat.items);
  const ids = items.map(it => it.id);
  const managerPct = (scores, mId) => evalScorePct(scores[mId], ids);

  // Cruce en memoria con los candidatos de la posición (nombre, foto…).
  const fresh = {};
  (rows || []).forEach(r => { fresh[String(r.id)] = r; });
  const dataOf = (c) => {
    const r = fresh[String(c.id)];
    return {
      managers: (r ? r.eval_managers : c.evalManagers) || [],
      scores: (r ? r.eval_scores : c.evalScores) || {},
      notes: (r ? r.eval_notes : c.evalNotes) || {},
    };
  };
  const all = (position.candidates || []).filter(c => !onlyCandidate || String(c.id) === String(onlyCandidate.id)).map(c => ({ c, ...dataOf(c) }));
  const evaluated = all
    .filter(x => x.managers.length > 0)
    .map(x => {
      const ps = x.managers.map(m => managerPct(x.scores, m.id)).filter(v => v !== null);
      return { ...x, pct: ps.length ? ps.reduce((a, b) => a + b, 0) / ps.length : null };
    })
    .sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1));

  // Guarda la evaluación del candidato en la base y en las dos copias en
  // memoria (esta pestaña y la lista de posiciones del resto de la app).
  const persist = async (c, transform) => {
    const { data: cur, error: readErr } = await supabase.from("candidates")
      .select("eval_managers, eval_scores, eval_notes").eq("id", c.id).maybeSingle();
    if (readErr || !cur) throw new Error(es ? "no se pudo leer la evaluación actual" : "couldn't read the current evaluation");
    const next = transform({ managers: cur.eval_managers || [], scores: cur.eval_scores || {}, notes: cur.eval_notes || {} });
    const now = new Date().toISOString();
    const { data, error } = await supabase.from("candidates").update({
      eval_managers: next.managers, eval_scores: next.scores, eval_notes: next.notes, eval_updated_at: now,
    }).eq("id", c.id).select("id");
    // RLS bloqueado = 200 con 0 filas: sin esta comprobación diría "guardado"
    if (error || !data || data.length === 0) throw new Error(error?.message || (es ? "sin permiso para guardar" : "no permission to save"));
    setRows(prev => {
      const list = prev || [];
      const row = { id: c.id, eval_managers: next.managers, eval_scores: next.scores, eval_notes: next.notes, eval_updated_at: now };
      return list.some(r => String(r.id) === String(c.id)) ? list.map(r => String(r.id) === String(c.id) ? row : r) : [...list, row];
    });
    onEvalChange(c.id, next);
    setOpenPositionsList(prev => prev.map(p => p.id !== position.id ? p : {
      ...p, candidates: (p.candidates || []).map(x => String(x.id) !== String(c.id) ? x
        : { ...x, evalManagers: next.managers, evalScores: next.scores, evalNotes: next.notes, evalUpdatedAt: now }),
    }));
  };

  const candidateOf = (id) => (position.candidates || []).find(x => String(x.id) === String(id));
  // En la ficha, su candidato; en la posición, el elegido en el formulario o
  // ninguno (se elige en la propia pantalla de entrevista)
  const openInterview = () => { setInterviewFor({ c: (onlyCandidate && candidateOf(onlyCandidate.id)) || onlyCandidate || candidateOf(form.candidateId) || null }); setMsg(null); };
  const filledCount = ids.filter(k => form.scores[k]).length;
  const canSave = form.candidateId && form.name.trim() && filledCount > 0 && !saving;

  const save = async () => {
    const c = candidateOf(form.candidateId);
    if (!c || !canSave) return;
    setSaving(true); setMsg(null);
    try {
      const managerId = editing ? editing.managerId : "m" + Date.now();
      await persist(c, ({ managers, scores, notes }) => ({
        managers: managers.some(m => m.id === managerId)
          ? managers.map(m => m.id === managerId ? { ...m, name: form.name.trim() } : m)
          : [...managers, { id: managerId, name: form.name.trim(), role: "" }],
        scores: { ...scores, [managerId]: Object.fromEntries(Object.entries(form.scores).filter(([k, v]) => v > 0 && ids.includes(k))) },
        notes: { ...notes, [managerId]: form.notes.trim() },
      }));
      setMsg({ ok: true, text: editing
        ? (es ? `Entrevista de ${c.name} actualizada.` : `${c.name}'s interview updated.`)
        : (es ? `Entrevista de ${c.name} guardada.` : `${c.name}'s interview saved.`) });
      setForm(blank); setEditing(null);
    } catch (e) {
      setMsg({ ok: false, text: (es ? "No se guardó: " : "Not saved: ") + e.message });
    }
    setSaving(false);
  };

  const startEdit = (c, m, scores, notes) => {
    setEditing({ candidateId: c.id, managerId: m.id });
    setForm({ candidateId: String(c.id), name: m.name || "", scores: { ...(scores[m.id] || {}) }, notes: notes[m.id] || "" });
    setMsg(null); setConfirmDel(null);
  };
  // El formulario solo existe al editar (31/10): en el móvil se lleva a la vista cuando aparece
  useEffect(() => {
    if (editing && isMobile && formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [editing, isMobile]);

  const remove = async () => {
    const { c, m } = confirmDel;
    setConfirmDel(null); setMsg(null);
    try {
      await persist(c, ({ managers, scores, notes }) => {
        const s = { ...scores }; delete s[m.id];
        const n = { ...notes }; delete n[m.id];
        return { managers: managers.filter(x => x.id !== m.id), scores: s, notes: n };
      });
      if (editing && editing.managerId === m.id) { setEditing(null); setForm(blank); }
    } catch (e) {
      setMsg({ ok: false, text: (es ? "No se eliminó: " : "Not deleted: ") + e.message });
    }
  };

  // ── Estilos ──
  const card = { background: WHITE, border: `1.5px solid ${LINE}`, borderRadius: 14 };
  const label = { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, display: "block", marginBottom: 6 };
  const control = { width: "100%", height: isMobile ? 44 : 38, boxSizing: "border-box", border: `1.5px solid ${LINE}`, borderRadius: 8, fontSize: isMobile ? 16 : 13, fontWeight: 500, fontFamily: "inherit", color: INK, background: WHITE, outline: "none", padding: "0 12px" };
  const th = { fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: 0.4, padding: "12px 8px", textAlign: "left", whiteSpace: "nowrap", borderBottom: `1.5px solid ${LINE}` };
  const td = { fontSize: 13, color: TEXT, padding: "11px 8px", borderBottom: `1px solid ${ROW_LINE}`, verticalAlign: "top" };
  const rowAction = () => ({ ...linkBtn, color: MUTED, fontWeight: 600 });

  const guideBody = guideFor && (
    <SimpleGuide key={guideFor.id} candidate={guideFor} position={position} lang={lang} bare focusSection={guideFocus}
      onStored={(json) => {
        setGuideFor(g => g ? { ...g, interviewerGuide: json } : g);
        setOpenPositionsList(prev => prev.map(p => p.id !== position.id ? p : { ...p, candidates: (p.candidates || []).map(x2 => String(x2.id) === String(guideFor.id) ? { ...x2, interviewerGuide: json } : x2) }));
      }} />
  );

  return (
    <div style={isMobile
      ? { display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }
      : { display: "grid", gridTemplateColumns: editing ? "minmax(280px, 330px) minmax(0, 1fr)" : "minmax(0, 1fr)", gap: 16, alignItems: "start" }}>
      {guideFor && isMobile && (
        // Móvil: la guía a pantalla completa, por encima de la barra inferior
        <div style={{ position: "fixed", inset: 0, background: WHITE, zIndex: 120, display: "flex", flexDirection: "column", paddingTop: "env(safe-area-inset-top)", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 8px 6px 16px", borderBottom: `1px solid ${LINE}`, flexShrink: 0 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...text.cardTitle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{es ? "Guía de entrevista" : "Interview guide"}</div>
              <div style={{ fontSize: 13, color: MUTED, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{guideFor.name}</div>
            </div>
            <CloseButton onClick={() => { setGuideFor(null); setGuideFocus(null); }} label={es ? "Cerrar" : "Close"} />
          </div>
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 16px calc(28px + env(safe-area-inset-bottom))" }}>
            {guideBody}
          </div>
        </div>
      )}
      {guideFor && !isMobile && (
        <div style={{ position: "fixed", top: 0, right: 0, height: "100vh", width: "min(440px, 92vw)", background: WHITE, borderLeft: `1.5px solid ${LINE}`, boxShadow: SHADOW_PANEL, zIndex: 400, overflowY: "auto", padding: "20px 24px 28px", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: MUTED }}>{guideFor.name}</span>
            <CloseButton onClick={() => { setGuideFor(null); setGuideFocus(null); }} label={es ? "Cerrar" : "Close"} />
          </div>
          {guideBody}
        </div>
      )}
      {editQuestions && (
        <QuestionsModal position={position} lang={lang} onClose={() => setEditQuestions(false)}
          onSaved={(cfg) => {
            setOpenPositionsList(prev => prev.map(p => p.id === position.id ? { ...p, evalQuestions: cfg } : p));
            setEditQuestions(false);
            setForm(f => ({ ...f, scores: {} }));
            setMsg({ ok: true, text: es ? "Preguntas guardadas." : "Questions saved." });
          }} />
      )}

      {/* En la ficha, la guía siempre a mano: se usa antes de la primera
          entrevista, cuando la tabla todavía está vacía. */}
      {/* Modo entrevista (puntuar MIENTRAS se entrevista, con la guía al
          lado): fuera del formulario, que es para apuntar después. En una
          fila propia, encima de las dos columnas: así el formulario y la
          tabla empiezan a la misma altura, sin escalón (24/10). */}
      {!isMobile && (
        <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
          <button onClick={() => setEditQuestions(true)} title={es ? "Editar preguntas y secciones" : "Edit questions and sections"}
            style={{ ...linkBtn, color: MUTED, fontWeight: 600, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5, marginRight: 8 }}>
            <Settings2 size={14} strokeWidth={1.75} /> {es ? "Preguntas" : "Questions"}
          </button>
          <button onClick={openInterview} style={{ ...btn.primary, ...btnSm }}>
            {es ? "Evaluar una entrevista" : "Evaluate an interview"}
          </button>
          {onlyCandidate && (
            <button onClick={() => setGuideFor(position.candidates.find(x => String(x.id) === String(onlyCandidate.id)) || onlyCandidate)}
              style={{ ...btn.soft, ...btnSm }}>
              {es ? "Guía de entrevista" : "Interview guide"}
            </button>
          )}
        </div>
      )}

      {/* ── Formulario ─────────────────────────────────────────────── */}
      {/* Fijo al hacer scroll: se evalúa mirando la tabla de la derecha.
          Si no cabe en la pantalla, scroll propio. */}
      {/* Móvil, en la ficha: la guía arriba del todo, a todo el ancho */}
      {isMobile && (
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={openInterview} style={{ ...btn.primary, flex: 1, height: 44, padding: 0 }}>
            {es ? "Evaluar una entrevista" : "Evaluate an interview"}
          </button>
          {onlyCandidate && (
            <button onClick={() => setGuideFor(position.candidates.find(x => String(x.id) === String(onlyCandidate.id)) || onlyCandidate)}
              style={{ ...btn.soft, flex: 1, height: 44, padding: 0 }}>
              {es ? "Guía de entrevista" : "Interview guide"}
            </button>
          )}
        </div>
      )}
      {interviewFor && (
        <InterviewMode position={position} candidate={interviewFor.c} candidates={onlyCandidate ? [] : (position.candidates || [])} lang={lang} photoUrls={photoUrls}
          initialName={form.name} persist={persist}
          onClose={(saved) => { setInterviewFor(null); if (saved) { setForm(blank); setMsg({ ok: true, text: es ? "Entrevista guardada." : "Interview saved." }); } }} />
      )}
      {/* En móvil: sin sticky ni scroll propio, va encima de los resultados.
          Solo al editar una evaluación hecha (31/10): las nuevas, con «Evaluar». */}
      {editing && <div ref={formRef} style={isMobile
        ? { ...card, padding: "16px 16px 18px", boxSizing: "border-box", scrollMarginTop: 8 }
        : { ...card, padding: "20px 20px 22px", position: "sticky", top: "calc(var(--pos-head, 0px) + 16px)", maxHeight: "calc(100vh - var(--pos-head, 0px) - 32px)", overflowY: "auto", boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, minHeight: isMobile ? 40 : undefined }}>
          <span style={text.cardTitle}>
            {editing ? (es ? "Editar evaluación" : "Edit evaluation") : (es ? "Nueva evaluación" : "New evaluation")}
          </span>
          {editing && (
            <button onClick={() => { setEditing(null); setForm(blank); setMsg(null); }}
              style={{ ...linkBtn, color: MUTED, fontWeight: 600, fontSize: isMobile ? 13 : 12, minHeight: isMobile ? 40 : undefined }}>
              {es ? "Cancelar" : "Cancel"}
            </button>
          )}
        </div>

        {!onlyCandidate && <>
        <label style={label}>{es ? "Candidato" : "Candidate"}</label>
        <select value={form.candidateId} disabled={!!editing}
          onChange={e => { setForm(f => ({ ...f, candidateId: e.target.value })); setMsg(null); }}
          style={{ ...control, marginBottom: 14, cursor: editing ? "default" : "pointer", color: form.candidateId ? INK : MUTED }}>
          <option value="">{es ? "Elige un candidato..." : "Choose a candidate..."}</option>
          {(position.candidates || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        </>}

        <label style={label}>{es ? "Entrevistador" : "Interviewer"}</label>
        <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          placeholder={es ? "Nombre o iniciales" : "Name or initials"}
          style={{ ...control, marginBottom: 6 }}
          onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />

        {categories.map(cat => (
          <div key={cat.id} style={{ paddingTop: 14, marginTop: 10, borderTop: `1px solid ${ROW_LINE}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
              {/* Título de sección en negro: el azul es solo para lo pulsable (DESIGN_SYSTEM) */}
              <div style={{ fontSize: 11, fontWeight: 800, color: INK, textTransform: "uppercase", letterSpacing: 0.6 }}>{cat.name}</div>
              {/* WTA = What to ask: abre la guía en las preguntas de este vector */}
              <button type="button" disabled={!form.candidateId}
                onClick={() => { const c = candidateOf(form.candidateId); if (c) { setGuideFocus(cat.id); setGuideFor(position.candidates.find(x => String(x.id) === String(c.id)) || c); } }}
                title={form.candidateId ? (es ? "Qué preguntar: preguntas de la guía para este vector" : "What to ask: guide questions for this vector") : (es ? "Elige antes un candidato" : "Choose a candidate first")}
                aria-label={es ? "Qué preguntar sobre " + cat.name : "What to ask about " + cat.name}
                style={{ width: isMobile ? 40 : 30, height: isMobile ? 40 : 30, borderRadius: "50%", border: `1.5px solid ${form.candidateId ? BRAND_LIGHT : LINE}`, background: guideFor && guideFocus === cat.id ? BRAND_LIGHT : WHITE, color: form.candidateId ? BRAND : FAINT, fontSize: 11, fontWeight: 800, letterSpacing: 0.3, cursor: form.candidateId ? "pointer" : "not-allowed", fontFamily: "inherit", padding: 0, flexShrink: 0 }}>
                WTA
              </button>
            </div>
            {cat.items.map(it => (
              <div key={it.id} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: isMobile ? 13 : 12, color: TEXT, marginBottom: 5, lineHeight: isMobile ? 1.45 : undefined }}>
                  <span style={{ color: FAINT, fontWeight: 700, marginRight: 6 }}>{it.short}</span>{it.label}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {[1, 2, 3].map(v => {
                    const on = form.scores[it.id] === v;
                    return (
                      <button key={v} onClick={() => setForm(f => ({ ...f, scores: { ...f.scores, [it.id]: on ? 0 : v } }))}
                        style={{ flex: 1, height: isMobile ? 44 : 32, borderRadius: 8, border: `1.5px solid ${on ? BRAND : LINE}`, background: on ? BRAND_LIGHT : WHITE, color: on ? BRAND : TEXT, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all 0.12s" }}>
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ))}

        <div style={{ paddingTop: 14, marginTop: 4, borderTop: `1px solid ${ROW_LINE}` }}>
          <label style={label}>{es ? "Comentarios" : "Comments"}</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3}
            placeholder={es ? "Impresiones de la entrevista..." : "Interview impressions..."}
            style={{ ...control, height: "auto", minHeight: isMobile ? 88 : undefined, padding: "10px 12px", resize: "vertical", lineHeight: 1.55 }}
            onFocus={e => e.target.style.borderColor = BRAND} onBlur={e => e.target.style.borderColor = LINE} />
        </div>

        {filledCount > 0 && filledCount < ids.length && (
          <div style={{ fontSize: 11, color: MUTED, marginTop: 8 }}>
            {es ? `${filledCount} de ${ids.length} preguntas puntuadas. Las vacías no cuentan en el %.` : `${filledCount} of ${ids.length} questions scored. Empty ones don't count towards the %.`}
          </div>
        )}

        <button onClick={save} disabled={!canSave}
          style={{ ...(canSave ? btn.primary : btn.disabled), width: "100%", marginTop: 14, height: isMobile ? 48 : 40, padding: 0, fontSize: isMobile ? 15 : 13 }}>
          {saving ? (es ? "Guardando..." : "Saving...") : editing ? (es ? "Guardar cambios" : "Save changes") : (es ? "Guardar evaluación" : "Save evaluation")}
        </button>
        {msg && <div style={{ ...(msg.ok ? savedText : errorText), marginTop: 10, lineHeight: 1.5 }}>{msg.ok ? "✓ " : ""}{msg.text}</div>}
      </div>}

      {/* ── Resultados ─────────────────────────────────────────────── */}
      <div style={{ minWidth: 0 }}>
        {/* El aviso de guardado, con el formulario cerrado, va aquí */}
        {!editing && msg && <div style={{ ...(msg.ok ? savedText : errorText), marginBottom: 10, lineHeight: 1.5 }}>{msg.ok ? "✓ " : ""}{msg.text}</div>}
        {loadError && (
          <div style={{ ...errorBanner, marginBottom: 12 }}>
            {es ? "No se pudo cargar la evaluación más reciente. Se muestra la que había al abrir la posición." : "Couldn't load the latest evaluations. Showing the data from when the position was opened."}
          </div>
        )}

        {/* Escritorio: ventana flotante sobre la pantalla (en el móvil, la barra va en la propia fila) */}
        {confirmDel && !isMobile && (
          <ConfirmDialog title={es ? "Eliminar entrevista" : "Delete interview"}
            message={es
              ? <>¿Eliminar la entrevista de <strong>{confirmDel.m.name || "—"}</strong> a <strong>{confirmDel.c.name}</strong>? Se borran sus puntuaciones y comentarios, y no se puede deshacer.</>
              : <>Delete <strong>{confirmDel.m.name || "—"}</strong>'s interview with <strong>{confirmDel.c.name}</strong>? Its scores and comments go with it, and it can't be undone.</>}
            actionLabel={es ? "Eliminar" : "Delete"} cancelLabel={es ? "Cancelar" : "Cancel"}
            onConfirm={remove} onCancel={() => setConfirmDel(null)} />
        )}

        {rows === null ? (
          <div style={{ ...card, padding: "28px 24px", ...text.body, color: MUTED }}>{es ? "Cargando evaluaciones..." : "Loading evaluations..."}</div>
        ) : evaluated.length === 0 ? (
          <div style={{ ...card, padding: "28px 24px", ...text.body, color: MUTED }}>
            {es
              ? (onlyCandidate ? "Todavía nadie ha evaluado a este candidato. Pulsa «Evaluar una entrevista» para puntuar la primera." : "Todavía no hay entrevistas evaluadas en esta posición. Pulsa «Evaluar una entrevista» para puntuar la primera.")
              : (onlyCandidate ? "Nobody has evaluated this candidate yet. Press “Evaluate an interview” to score the first one." : "No interviews have been evaluated for this position yet. Press “Evaluate an interview” to score the first one.")}
          </div>
        ) : isMobile ? (
          // Móvil: una tarjeta, un bloque por candidato (cabecera con su % y la
          // guía) y debajo una fila por entrevista. Sin tabla de columnas.
          <div style={{ ...card, overflow: "hidden" }}>
            {evaluated.map(({ c, managers, scores, notes, pct }, idx) => (
              <div key={c.id} style={{ borderTop: idx ? `1.5px solid ${LINE}` : "none" }}>
                <div style={{ background: SURFACE, padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    {photoUrls[c.id]
                      ? <img src={photoUrls[c.id]} alt={c.name} style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, display: "block", flexShrink: 0 }} />
                      : <CandidateAvatar name={c.name} size={36} />}
                    <div style={{ minWidth: 0, flex: 1 }}>
                      {onlyCandidate
                        ? <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>{c.name}</div>
                        : <button onClick={() => onOpenCandidate(c)}
                          style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 15, fontWeight: 800, color: BRAND, textAlign: "left", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{c.name}</button>}
                      <div style={{ fontSize: 13, color: MUTED, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {managers.length} {managers.length === 1 ? (es ? "entrevista" : "interview") : (es ? "entrevistas" : "interviews")}
                        {(c.position || c.company) ? " · " : ""}{[c.position, c.company].filter(Boolean).join(" · ")}
                      </div>
                    </div>
                    <PctChip pct={pct} big />
                  </div>
                  {!onlyCandidate && (
                    <button onClick={() => { setGuideFocus(null); setGuideFor(c); }}
                      style={{ ...btn.soft, width: "100%", height: 40, marginTop: 12, padding: 0 }}>
                      {es ? "Guía de entrevista" : "Interview guide"}
                    </button>
                  )}
                </div>
                {managers.map(m => {
                  const s = scores[m.id] || {};
                  const isEditing = editing && editing.managerId === m.id && String(editing.candidateId) === String(c.id);
                  const asking = confirmDel && confirmDel.m.id === m.id && String(confirmDel.c.id) === String(c.id);
                  return (
                    <div key={m.id} style={{ padding: "12px 16px 14px", borderTop: `1px solid ${ROW_LINE}`, background: isEditing ? HOVER_BG : "transparent" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        <span style={{ fontSize: 15, fontWeight: 800, color: INK, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.name || "—"}</span>
                        <PctChip pct={managerPct(scores, m.id)} />
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                        {items.map(it => (
                          <span key={it.id} style={{ ...chip(false), fontVariantNumeric: "tabular-nums" }}>
                            <span style={{ color: MUTED, fontWeight: 700 }}>{it.short}</span>{" "}
                            <span style={{ color: s[it.id] ? INK : FAINT, fontWeight: s[it.id] ? 700 : 500 }}>{s[it.id] || "—"}</span>
                          </span>
                        ))}
                      </div>
                      {notes[m.id] && <div style={{ fontSize: 13, color: TEXT, lineHeight: 1.55, whiteSpace: "pre-wrap", marginTop: 8 }}>{notes[m.id]}</div>}
                      {asking ? (
                        <ConfirmBar style={{ marginTop: 10 }}
                          message={es
                            ? <>¿Eliminar la entrevista de <strong>{confirmDel.m.name || "—"}</strong> a <strong>{confirmDel.c.name}</strong>? Se borran sus puntuaciones y comentarios, y no se puede deshacer.</>
                            : <>Delete <strong>{confirmDel.m.name || "—"}</strong>'s interview with <strong>{confirmDel.c.name}</strong>? Its scores and comments go with it, and it can't be undone.</>}
                          actionLabel={es ? "Eliminar" : "Delete"} cancelLabel={es ? "Cancelar" : "Cancel"}
                          onConfirm={remove} onCancel={() => setConfirmDel(null)} />
                      ) : (
                        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                          <button onClick={() => startEdit(c, m, scores, notes)} style={{ ...btn.ghost, flex: 1, height: 40, padding: 0 }}>
                            {es ? "Editar" : "Edit"}
                          </button>
                          <button onClick={() => { setConfirmDel({ c, m }); setMsg(null); }} style={{ ...btn.ghost, flex: 1, height: 40, padding: 0 }}>
                            {es ? "Eliminar" : "Delete"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            {/* Leyenda de las siglas */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "12px 16px 14px", fontSize: 12, color: MUTED, borderTop: `1.5px solid ${LINE}`, lineHeight: 1.45 }}>
              {items.map(it => <span key={it.id}><strong style={{ color: TEXT }}>{it.short}</strong> {it.label}</span>)}
              <span style={{ marginTop: 4 }}>{es ? "Escala 1-3 · % sobre el máximo de las preguntas puntuadas" : "1-3 scale · % of the maximum over the scored questions"}</span>
            </div>
          </div>
        ) : (
          <div style={{ ...card, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
              <thead>
                <tr>
                  <th style={{ ...th, paddingLeft: 18 }}>{es ? "Entrevistador" : "Interviewer"}</th>
                  {items.map(it => <th key={it.id} title={it.label} style={{ ...th, textAlign: "center", cursor: "help" }}>{it.short}</th>)}
                  <th style={th}>{es ? "Comentarios" : "Comments"}</th>
                  <th style={{ ...th, textAlign: "center" }}>%</th>
                  <th style={{ ...th, paddingRight: 18 }} />
                </tr>
              </thead>
              <tbody>
                {evaluated.map(({ c, managers, scores, notes, pct }) => [
                  <tr key={`h-${c.id}`} style={{ background: SURFACE }}>
                    <td colSpan={items.length + 2} style={{ ...td, paddingLeft: 18, borderBottom: `1px solid ${LINE}`, verticalAlign: "middle" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {photoUrls[c.id]
                          ? <img src={photoUrls[c.id]} alt={c.name} style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${LINE}`, display: "block", flexShrink: 0 }} />
                          : <CandidateAvatar name={c.name} size={32} />}
                        <div style={{ minWidth: 0 }}>
                          {onlyCandidate
                            ? <span style={{ fontSize: 13, fontWeight: 800, color: INK }}>{c.name}</span>
                            : <button onClick={() => onOpenCandidate(c)} title={es ? "Abrir ficha" : "Open profile"}
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 800, color: BRAND, textAlign: "left" }}>{c.name}</button>}
                          <div style={{ fontSize: 11, color: MUTED, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {[c.position, c.company].filter(Boolean).join(" · ")}{(c.position || c.company) ? " · " : ""}
                            {managers.length} {managers.length === 1 ? (es ? "entrevista" : "interview") : (es ? "entrevistas" : "interviews")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...td, textAlign: "center", borderBottom: `1px solid ${LINE}`, verticalAlign: "middle" }}><PctChip pct={pct} big /></td>
                    <td style={{ ...td, borderBottom: `1px solid ${LINE}`, verticalAlign: "middle", textAlign: "right", paddingRight: 18, whiteSpace: "nowrap" }}>
                      {!onlyCandidate && <button onClick={() => { setGuideFocus(null); setGuideFor(c); }}
                        style={{ ...btn.soft, ...btnSm }}>
                        {es ? "Guía de entrevista" : "Interview guide"}
                      </button>}
                    </td>
                  </tr>,
                  ...managers.map(m => {
                    const s = scores[m.id] || {};
                    const isEditing = editing && editing.managerId === m.id && String(editing.candidateId) === String(c.id);
                    return (
                      <tr key={`${c.id}-${m.id}`} style={{ background: isEditing ? HOVER_BG : "transparent" }}>
                        <td style={{ ...td, paddingLeft: 18, color: INK, fontWeight: 600, whiteSpace: "nowrap" }}>{m.name || "—"}</td>
                        {items.map(it => (
                          <td key={it.id} style={{ ...td, textAlign: "center", color: s[it.id] ? INK : FAINT, fontWeight: s[it.id] ? 700 : 500 }}>{s[it.id] || "—"}</td>
                        ))}
                        <td style={{ ...td, lineHeight: 1.55, minWidth: 200, maxWidth: 380, whiteSpace: "pre-wrap" }}>
                          {notes[m.id] ? notes[m.id] : <span style={{ color: FAINT }}>—</span>}
                        </td>
                        <td style={{ ...td, textAlign: "center" }}><PctChip pct={managerPct(scores, m.id)} /></td>
                        <td style={{ ...td, paddingRight: 18, whiteSpace: "nowrap", textAlign: "right" }}>
                          <button onClick={() => startEdit(c, m, scores, notes)} style={rowAction()}
                            onMouseEnter={e => e.currentTarget.style.color = BRAND} onMouseLeave={e => e.currentTarget.style.color = MUTED}>
                            {es ? "Editar" : "Edit"}
                          </button>
                          <button onClick={() => { setConfirmDel({ c, m }); setMsg(null); }} style={{ ...rowAction(), marginLeft: 12 }}
                            onMouseEnter={e => e.currentTarget.style.color = BRAND} onMouseLeave={e => e.currentTarget.style.color = MUTED}>
                            {es ? "Eliminar" : "Delete"}
                          </button>
                        </td>
                      </tr>
                    );
                  }),
                ])}
              </tbody>
            </table>
            {/* Leyenda de las siglas */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 18px", padding: "12px 18px", fontSize: 11, color: MUTED }}>
              {items.map(it => <span key={it.id}><strong style={{ color: TEXT }}>{it.short}</strong> {it.label}</span>)}
              <span>{es ? "Escala 1-3 · % sobre el máximo de las preguntas puntuadas" : "1-3 scale · % of the maximum over the scored questions"}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PositionEvaluationTab;
